import { useDialogFocus } from '../hooks/useDialogFocus';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './Calendar.css';
import { apiFetch } from '../api';
import { useMediaQuery } from '../hooks/useMediaQuery';

const API_BASE = '/api/calendar';
const localBoundary = date => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}T${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}:${String(date.getSeconds()).padStart(2,'0')}`;
const compactTitle = event => event.title.replace(/^(Оплата:|Пополнить баланс:)\s*/, '').replace(/^Оплата\s+/i, '').replace(/^квартиры$/i, 'Квартира').replace(/^телефона$/i, 'Телефон');
const readableDescription = event => (event.description || '').split('\n').filter(line => !/^(Объект затрат id=|Расписание:|Ближайший плановый срок по графику:|Прогноз исчерпания баланса:)/.test(line)).join('\n');


const VIEWS = ['agenda', 'month', 'week', 'day', 'list'];
const VIEW_LABELS = { agenda: 'Повестка', list: 'Список', month: 'Месяц', week: 'Неделя', day: 'День' };

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
/** Базовый диапазон сетки дня/недели: [8, 19) — часы 8:00…18:00, нижняя граница шкалы 19:00. */
const DEFAULT_WEEKDAY_GRID_START = 8;
const DEFAULT_WEEKDAY_GRID_END = 19;

const getWeekStart = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Понедельник как начало недели
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getWeekDays = (weekStart) =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

const getMonthDays = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const start = getWeekStart(first);
  const end = new Date(getWeekStart(last));
  end.setDate(end.getDate() + 6);
  const days = [];
  const d = new Date(start);
  while (d <= end) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const getHourFractionFromEvent = (e, slotsEl, hourStart, hourEnd) => {
  if (!slotsEl) return null;
  const rect = slotsEl.getBoundingClientRect();
  const y = e.clientY - rect.top;
  const h = rect.height;
  if (y < 0 || y > h) return null;
  const fraction = y / h;
  const hour = hourStart + fraction * (hourEnd - hourStart);
  return Math.max(hourStart, Math.min(hourEnd, hour));
};

const getPositionFromClient = (clientX, clientY, hourStart, hourEnd) => {
  const el = document.elementFromPoint(clientX, clientY);
  const col = el?.closest('.calendar-time-col');
  if (!col || col.classList.contains('calendar-time-col--header')) return null;
  const dayKey = col.dataset.dayKey;
  const slots = col.querySelector('.calendar-time-slots');
  if (!slots) return null;
  const rect = slots.getBoundingClientRect();
  const y = clientY - rect.top;
  const h = rect.height;
  if (y < 0 || y > h) return null;
  const fraction = y / h;
  const hour = hourStart + fraction * (hourEnd - hourStart);
  const hourFrac = Math.max(hourStart, Math.min(hourEnd, hour));
  return { dayKey, hourFrac };
};

const roundMinutesTo10 = (ms) => {
  const d = new Date(ms);
  let m = d.getMinutes();
  m = Math.round(m / 10) * 10;
  if (m >= 60) {
    d.setHours(d.getHours() + 1);
    m = 0;
  }
  d.setMinutes(m, 0, 0);
  return d.getTime();
};

const hourFractionToDatetime = (dayDate, hourFraction) => {
  const d = new Date(dayDate);
  let h = Math.floor(hourFraction);
  let m = Math.round((hourFraction - h) * 60);
  m = Math.round(m / 10) * 10;
  if (m >= 60) {
    h += 1;
    m = 0;
  }
  d.setHours(h, m, 0, 0);
  return d;
};

const formatDatetimeForApi = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00+03:00`;
};

function CalendarDialog({ children, className, label, onClose }) {
  const ref = useRef(null);
  useDialogFocus(ref, onClose);
  return <div ref={ref} className={className} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onClick={e => e.stopPropagation()}>{children}</div>;
}

const Calendar = ({ refreshKey = 0 }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => window.matchMedia?.('(max-width: 768px)').matches ? 'agenda' : 'month');
  const [focusDate, setFocusDate] = useState(new Date());
  const [createForm, setCreateForm] = useState(null); // { startTime, endTime }
  const [detailEvent, setDetailEvent] = useState(null);
  const [editForm, setEditForm] = useState(null); // event object
  const [dragState, setDragState] = useState(null); // { startDayKey, startHour, currentDayKey, currentHour }
  const [shiftDragState, setShiftDragState] = useState(null); // { type, event, currentStart, currentEnd }
  const [eventHoverZone, setEventHoverZone] = useState(null); // { id, zone: 'top'|'body'|'bottom' }
  const [selectedDayKey, setSelectedDayKey] = useState(null); // mobile month: day key for events panel
  const skipEditClickRef = useRef(false);
  const longPressTimerRef = useRef(null);
  const longPressCleanupRef = useRef(null);
  const viewRef = useRef(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const { startStr, endStr } = useMemo(() => {
    const focus = new Date(focusDate);
    focus.setHours(0, 0, 0, 0);

    if (view === 'day' && !isMobile) {
      const start = new Date(focus);
      const end = new Date(focus);
      end.setHours(23, 59, 59, 999);
      return {
        startStr: localBoundary(start),
        endStr: localBoundary(end),
      };
    }

    if (view === 'week' || view === 'agenda' || (view === 'day' && isMobile)) {
      const weekStart = getWeekStart(focus);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      return {
        startStr: localBoundary(weekStart),
        endStr: localBoundary(weekEnd),
      };
    }

    // list, month
    const visibleDays = getMonthDays(focus.getFullYear(), focus.getMonth());
    const start = view === 'month' ? visibleDays[0] : new Date(focus.getFullYear(), focus.getMonth(), 1);
    const end = view === 'month' ? new Date(visibleDays[visibleDays.length - 1]) : new Date(focus.getFullYear(), focus.getMonth() + 1, 0);
    end.setHours(23, 59, 59);
    return {
      startStr: localBoundary(start),
      endStr: localBoundary(end),
    };
  }, [view, focusDate, isMobile]);

  /** Подпись периода под кнопками видов. */
  const headerPeriodSpec = useMemo(() => {
    const focus = new Date(focusDate);
    focus.setHours(0, 0, 0, 0);

    if (view === 'list') {
      const raw = focus.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      const text = raw.charAt(0).toUpperCase() + raw.slice(1);
      return { variant: 'plain', text };
    }

    if (view === 'month') {
      const raw = focus.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      const text = raw.charAt(0).toUpperCase() + raw.slice(1);
      return { variant: 'plain', text };
    }

    if (view === 'week' || view === 'agenda') {
      const weekStart = getWeekStart(focus);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const y1 = weekStart.getFullYear();
      const m1 = weekStart.getMonth();
      const d1 = weekStart.getDate();
      const y2 = weekEnd.getFullYear();
      const m2 = weekEnd.getMonth();
      const d2 = weekEnd.getDate();
      let text;
      if (y1 === y2 && m1 === m2) {
        const mon = weekStart.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }).replace(/^\d+\s+/, '');
        text = `${d1}–${d2} ${mon} ${y1}`;
      } else if (y1 === y2) {
        const mS = weekStart.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }).replace(/^\d+\s+/, '');
        const mE = weekEnd.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }).replace(/^\d+\s+/, '');
        text = `${d1} ${mS} — ${d2} ${mE} ${y1}`;
      } else {
        const mS = weekStart.toLocaleDateString('ru-RU', { month: 'short' });
        const mE = weekEnd.toLocaleDateString('ru-RU', { month: 'short' });
        text = `${d1} ${mS} ${y1} — ${d2} ${mE} ${y2}`;
      }
      return { variant: 'plain', text };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayDiff = Math.round((focus.getTime() - today.getTime()) / 86400000);
    const REL_WORDS = {
      0: 'Сегодня',
      1: 'Завтра',
      2: 'Послезавтра',
      [-1]: 'Вчера',
      [-2]: 'Позавчера',
    };
    if (REL_WORDS[dayDiff] !== undefined) {
      const sub = focus.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
      return { variant: 'dayRelative', title: REL_WORDS[dayDiff], sub };
    }
    const main = focus.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    const year = focus.getFullYear();
    return { variant: 'dayAbsolute', main, year };
  }, [view, focusDate]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(
        `${API_BASE}/events/?start_date=${startStr}&end_date=${endStr}`
      );
      if (response.status === 401) throw new Error('Требуется авторизация');
      if (!response.ok) throw new Error('Ошибка загрузки событий');
      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [startStr, endStr]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, refreshKey]);

  const formatTime = (str) => {
    if (!str) return '';
    return new Date(str).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const toDateKey = (d) => {
    const x = d instanceof Date ? d : new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };

  const isSameDay = (a, b) => toDateKey(a) === toDateKey(b);
  const isOtherMonth = (d, y, m) => d.getMonth() !== m || d.getFullYear() !== y;

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      const key = toDateKey(ev.start_time);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [events]);

  const weekDayVisibleRange = useMemo(() => {
    const defS = DEFAULT_WEEKDAY_GRID_START;
    const defE = DEFAULT_WEEKDAY_GRID_END;
    const baseHours = () => Array.from({ length: defE - defS }, (_, i) => defS + i);
    if (view !== 'week' && view !== 'day') {
      return { gridStart: defS, gridEnd: defE, hours: baseHours() };
    }
    const focus = new Date(focusDate);
    focus.setHours(0, 0, 0, 0);
    const days = view === 'week' ? getWeekDays(getWeekStart(focus)) : [new Date(focus)];
    const dayKeys = new Set(days.map((d) => toDateKey(d)));

    let minH = Number.POSITIVE_INFINITY;
    let maxH = Number.NEGATIVE_INFINITY;
    const push = (h) => {
      if (Number.isFinite(h)) {
        minH = Math.min(minH, h);
        maxH = Math.max(maxH, h);
      }
    };

    events.forEach((ev) => {
      const sk = toDateKey(ev.start_time);
      if (!dayKeys.has(sk)) return;
      const s = new Date(ev.start_time);
      const e = ev.end_time ? new Date(ev.end_time) : new Date(s.getTime() + 60 * 60 * 1000);
      push(s.getHours() + s.getMinutes() / 60 + s.getSeconds() / 3600);
      if (toDateKey(e) === sk) {
        push(e.getHours() + e.getMinutes() / 60 + e.getSeconds() / 3600);
      } else {
        push(24);
      }
    });

    if (!Number.isFinite(minH)) {
      return { gridStart: defS, gridEnd: defE, hours: baseHours() };
    }

    let gridStart = Math.min(defS, Math.floor(minH));
    let gridEnd = Math.max(defE, Math.ceil(maxH));
    gridStart = Math.max(0, Math.min(23, gridStart));
    gridEnd = Math.min(24, Math.max(gridEnd, gridStart + 1));
    const hours = Array.from({ length: gridEnd - gridStart }, (_, i) => gridStart + i);
    return { gridStart, gridEnd, hours };
  }, [view, focusDate, events]);

  const weekDayRangeRef = useRef(weekDayVisibleRange);
  useEffect(() => {
    weekDayRangeRef.current = weekDayVisibleRange;
  }, [weekDayVisibleRange]);

  const prev = useCallback(() => {
    setFocusDate((focusDate) => {
      const d = new Date(focusDate);
      const v = viewRef.current;
      if (v === 'day') d.setDate(d.getDate() - 1);
      else if (v === 'week' || v === 'agenda') d.setDate(d.getDate() - 7);
      else { d.setDate(1); d.setMonth(d.getMonth() - 1); }
      return d;
    });
  }, []);

  const next = useCallback(() => {
    setFocusDate((focusDate) => {
      const d = new Date(focusDate);
      const v = viewRef.current;
      if (v === 'day') d.setDate(d.getDate() + 1);
      else if (v === 'week' || v === 'agenda') d.setDate(d.getDate() + 7);
      else { d.setDate(1); d.setMonth(d.getMonth() + 1); }
      return d;
    });
  }, []);

  const today = () => setFocusDate(new Date());

  const createEvent = async (title, description, startTime, endTime) => {
    try {
      const res = await apiFetch(`${API_BASE}/events/create/`, {
        method: 'POST',
        body: JSON.stringify({
          title,
          description: description || '',
          start_time: startTime,
          end_time: endTime,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Ошибка создания');
      }
      setCreateForm(null);
      fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const title = fd.get('title')?.trim();
    if (!title) return;
    const start = new Date(fd.get('start_time'));
    const end = new Date(fd.get('end_time'));
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) { window.alert('Конец должен быть позже начала'); return; }
    createEvent(title, fd.get('description') || '', formatDatetimeForApi(start), formatDatetimeForApi(end));
  };

  const toDatetimeLocal = (isoStr) => (isoStr || '').slice(0, 16);

  const updateEvent = useCallback(async (eventId, { title, description, start_time, end_time }) => {
    try {
      const res = await apiFetch(`${API_BASE}/events/${eventId}/update/`, {
        method: 'PATCH',
        body: JSON.stringify({ title, description, start_time, end_time }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Ошибка сохранения');
      }
      setEditForm(null);
      fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  }, [fetchEvents]);

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Удалить событие?')) return;
    try {
      const res = await apiFetch(`${API_BASE}/events/${eventId}/delete/`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Ошибка удаления');
      setEditForm(null);
      fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const title = fd.get('title')?.trim();
    if (!title || !editForm) return;
    updateEvent(editForm.id, {
      title,
      description: fd.get('description') || '',
      start_time: fd.get('start_time') ? `${fd.get('start_time')}:00+03:00` : editForm.start_time,
      end_time: fd.get('end_time') ? `${fd.get('end_time')}:00+03:00` : null,
    });
  };

  const EDGE_ZONE = 0.25;

  const getClientPos = useCallback((e) => {
    if (e.touches?.[0]) return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    if (e.changedTouches?.[0]) return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
    return { clientX: e.clientX, clientY: e.clientY };
  }, []);

  const handleEventClick = useCallback((ev) => {
    if (skipEditClickRef.current) {
      skipEditClickRef.current = false;
      return;
    }
    setDetailEvent(ev);
  }, []);

  const listGroups = useMemo(() => {
    const todayKey = toDateKey(new Date());
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = toDateKey(tomorrow);
    const getLabel = (dateKey) => {
      if (dateKey === todayKey) return 'Сегодня';
      if (dateKey === tomorrowKey) return 'Завтра';
      const [y, m, d] = dateKey.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    };
    const keys = Object.keys(eventsByDate).sort();
    return keys.map((key) => ({
      key,
      label: getLabel(key),
      events: (eventsByDate[key] || []).sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    }));
  }, [eventsByDate]);

  // --- Render views ---
  const renderList = () => (
    <div className="calendar-events-list-wrapper">
      {listGroups.map(({ key, label, events: groupEvents }) => (
        <div key={key} className="calendar-list-group">
          <h3 className="calendar-list-group-title">{label}</h3>
          <ul className="calendar-events-list">
            {groupEvents.map((event) => (
              <li
                key={event.id}
                className={`calendar-event calendar-event--clickable event-kind--${event.event_type || "event"}`}
                onClick={() => setDetailEvent(event)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setDetailEvent(event)}
              >
                <div className="calendar-event-title">{compactTitle(event)}</div>
                <div className="calendar-event-time">
                  {formatTime(event.start_time)}
                  {event.end_time && ` — ${formatTime(event.end_time)}`}
                </div>
                {event.description && (
                  <div className="calendar-event-desc">{readableDescription(event)}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const renderMonth = () => {
    const year = focusDate.getFullYear();
    const month = focusDate.getMonth();
    const days = getMonthDays(year, month);
    const previewLimit = isMobile ? 2 : 3;
    const detailKey = selectedDayKey || toDateKey(focusDate);
    const detailEvents = [...(eventsByDate[detailKey] || [])].sort((a,b) => new Date(a.start_time) - new Date(b.start_time));
    return (
      <div className="calendar-month-layout"><div className="calendar-month-grid" style={{'--week-count': days.length / 7}}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="calendar-month-weekday">
            {w}
          </div>
        ))}
        {days.map((d, i) => {
          const key = toDateKey(d);
          const dayEvents = eventsByDate[key] || [];
          const other = isOtherMonth(d, year, month);
          return (
            <div
              key={i}
              aria-label={d.toLocaleDateString('ru-RU', {day:'numeric',month:'long',year:'numeric'})}
              className={`calendar-month-day ${key === selectedDayKey ? 'calendar-month-day--selected' : ''} ${other ? 'calendar-month-day--other' : ''} ${isSameDay(d, new Date()) ? 'calendar-month-day--today' : ''} ${isMobile ? 'calendar-month-day--clickable' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedDayKey(key)}
              onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedDayKey(key); } }}
            >
              <div className="calendar-day-number"><span className="calendar-month-day-num">{d.getDate()}</span><small aria-label={`${dayEvents.length} событий`}>{dayEvents.length || ""}</small></div>
              <div className="calendar-month-day-events">
                {dayEvents.slice(0, previewLimit).map((ev) => (
                  <button type="button"
                    key={ev.id}
                    className={`calendar-month-event calendar-month-event--clickable event-kind--${ev.event_type || "event"}`}
                    title={ev.title}
                    aria-label={`${ev.title}, ${formatTime(ev.start_time)}`}
                    onClick={(e) => { e.stopPropagation(); setDetailEvent(ev); }}
                  >
                    <span className="calendar-month-event-title">{compactTitle(ev)}</span>
                  </button>
                ))}
                {dayEvents.length > previewLimit && (
                  <div className="calendar-month-event calendar-month-event--more">
                    +{dayEvents.length - previewLimit}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!isMobile && (selectedDayKey || detailEvents.length > 0) && <aside className="calendar-month-detail"><h3>{new Date(`${detailKey}T12:00:00`).toLocaleDateString('ru-RU',{day:'numeric',month:'long'})}</h3><p className="calendar-month-detail-label">События выбранного дня</p>{detailEvents.length ? detailEvents.map(ev => <button key={ev.id} onClick={() => setDetailEvent(ev)}><time>{formatTime(ev.start_time)}</time><strong>{compactTitle(ev)}</strong></button>) : <p>Пока свободно. Выберите день в календаре или добавьте событие.</p>}</aside>}
      </div>
    );
  };

  const dayKeyToDate = useCallback((key) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, []);

  const handleEventMouseMove = useCallback((e, eventId) => {
    if (isMobile || !e.shiftKey) {
      setEventHoverZone(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const frac = y / rect.height;
    let zone = 'body';
    if (frac < EDGE_ZONE) zone = 'top';
    else if (frac > 1 - EDGE_ZONE) zone = 'bottom';
    setEventHoverZone({ id: eventId, zone });
  }, [isMobile]);

  const handleEventMouseLeave = useCallback(() => {
    setEventHoverZone(null);
  }, []);

  useEffect(() => {
    const onKeyUp = (e) => { if (e.key === 'Shift') setEventHoverZone(null); };
    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, []);

  const startShiftDrag = useCallback((ev, clientX, clientY, rect) => {
    const y = clientY - rect.top;
    const frac = y / rect.height;
    let type = 'move';
    if (frac < EDGE_ZONE) type = 'resizeTop';
    else if (frac > 1 - EDGE_ZONE) type = 'resizeBottom';
    const start = new Date(ev.start_time);
    const end = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
    const { gridStart, gridEnd } = weekDayRangeRef.current;
    const pos = getPositionFromClient(clientX, clientY, gridStart, gridEnd);
    if (!pos) return;
    skipEditClickRef.current = true;
    const dur = end.getTime() - start.getTime();
    setShiftDragState({
      type,
      event: ev,
      originStart: start.getTime(),
      originEnd: end.getTime(),
      originDayKey: pos.dayKey,
      originHour: pos.hourFrac,
      originOffsetY: frac,
      duration: dur,
      currentStart: start.getTime(),
      currentEnd: end.getTime(),
    });
  }, []);

  const handleEventMouseDown = useCallback((e, ev) => {
    if (e.button !== 0) return;
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const clientX = e.clientX;
      const clientY = e.clientY;
      longPressCleanupRef.current?.();
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTimerRef.current = null;
        longPressCleanupRef.current?.();
        longPressCleanupRef.current = null;
        startShiftDrag(ev, clientX, clientY, rect);
      }, 1000);
      const onUp = () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        longPressCleanupRef.current = null;
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('mousemove', onMove);
      };
      const onMove = () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          longPressCleanupRef.current = null;
          document.removeEventListener('mouseup', onUp);
          document.removeEventListener('mousemove', onMove);
        }
      };
      longPressCleanupRef.current = () => {
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('mousemove', onMove);
      };
      document.addEventListener('mouseup', onUp);
      document.addEventListener('mousemove', onMove);
      return;
    }
    if (!e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    startShiftDrag(ev, e.clientX, e.clientY, rect);
  }, [isMobile, startShiftDrag]);

  const handleEventTouchStart = useCallback((e, ev) => {
    if (!isMobile || !e.touches?.[0]) return;
    // Не preventDefault: иначе ломается вертикальный скролл .calendar-content (браузер не получает пан-скролл).
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = e.touches[0];
    longPressCleanupRef.current?.();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null;
      longPressCleanupRef.current?.();
      longPressCleanupRef.current = null;
      startShiftDrag(ev, clientX, clientY, rect);
    }, 1000);
    const onTouchMove = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        longPressCleanupRef.current = null;
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        document.removeEventListener('touchcancel', onTouchEnd);
      }
    };
    const onTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        longPressCleanupRef.current = null;
      }
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
    longPressCleanupRef.current = () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  }, [isMobile, startShiftDrag]);

  useEffect(() => {
    if (!shiftDragState) return;
    const onMove = (e2) => {
      if (e2.touches?.length) e2.preventDefault();
      const { clientX, clientY } = getClientPos(e2);
      const { gridStart, gridEnd } = weekDayRangeRef.current;
      const pos = getPositionFromClient(clientX, clientY, gridStart, gridEnd);
      if (!pos) return;
      const dayDate = dayKeyToDate(pos.dayKey);
      const cursorTime = hourFractionToDatetime(dayDate, pos.hourFrac).getTime();
      const duration = shiftDragState.originEnd - shiftDragState.originStart;
      let newStart, newEnd;
      if (shiftDragState.type === 'move') {
        const anchorAtCursor = cursorTime - shiftDragState.originOffsetY * duration;
        newStart = roundMinutesTo10(anchorAtCursor);
        newEnd = newStart + duration;
      } else if (shiftDragState.type === 'resizeTop') {
        newStart = Math.min(cursorTime, shiftDragState.originEnd - 10 * 60 * 1000);
        newEnd = shiftDragState.originEnd;
      } else {
        newStart = shiftDragState.originStart;
        newEnd = Math.max(cursorTime, shiftDragState.originStart + 10 * 60 * 1000);
      }
      setShiftDragState((prev) => prev ? { ...prev, currentStart: newStart, currentEnd: newEnd } : null);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      setShiftDragState((prev) => {
        if (!prev) return null;
        const s = prev.currentStart ?? prev.originStart;
        const end = prev.currentEnd ?? prev.originEnd;
        updateEvent(prev.event.id, {
          title: prev.event.title,
          description: prev.event.description || '',
          start_time: formatDatetimeForApi(new Date(s)),
          end_time: formatDatetimeForApi(new Date(end)),
        });
        return null;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [shiftDragState, dayKeyToDate, updateEvent, getClientPos]);

  const handleSlotsMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      const col = e.target.closest('.calendar-time-col');
      if (!col || col.classList.contains('calendar-time-col--header')) return;
      const { gridStart, gridEnd } = weekDayRangeRef.current;
      const dayKey = col.dataset.dayKey;
      const slots = col.querySelector('.calendar-time-slots');
      const hourFrac = getHourFractionFromEvent(e, slots, gridStart, gridEnd);
      if (hourFrac == null) return;
      setDragState({ startDayKey: dayKey, startHour: hourFrac, currentDayKey: dayKey, currentHour: hourFrac });

      const onMove = (e2) => {
        const el = document.elementFromPoint(e2.clientX, e2.clientY);
        const c = el?.closest('.calendar-time-col');
        if (!c || c.classList.contains('calendar-time-col--header')) return;
        const dk = c.dataset.dayKey;
        const sl = c.querySelector('.calendar-time-slots');
        const hf = getHourFractionFromEvent(e2, sl, gridStart, gridEnd);
        if (hf != null)
          setDragState((prev) => (prev ? { ...prev, currentDayKey: dk, currentHour: hf } : null));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        setDragState((prev) => {
          if (!prev) return null;
          const startDt = hourFractionToDatetime(dayKeyToDate(prev.startDayKey), prev.startHour);
          const endDt = hourFractionToDatetime(dayKeyToDate(prev.currentDayKey), prev.currentHour);
          let [s, end] = startDt <= endDt ? [startDt, endDt] : [endDt, startDt];
          const minDuration = 5 / 60;
          if ((end - s) / (60 * 60 * 1000) < minDuration) end = new Date(s.getTime() + 30 * 60 * 1000);
          setCreateForm({
            startTime: formatDatetimeForApi(s),
            endTime: formatDatetimeForApi(end),
          });
          return null;
        });
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [dayKeyToDate]
  );

  const renderWeekOrDay = (isWeek) => {
    const { gridStart, gridEnd, hours: HOURS } = weekDayVisibleRange;
    const HOURS_COUNT = gridEnd - gridStart;
    const getEventPositionForTimes = (dayDate, start, end) => {
      if (!isSameDay(start, dayDate)) return null;
      const startHours = start.getHours() + start.getMinutes() / 60 + start.getSeconds() / 3600;
      const endHours = end.getHours() + end.getMinutes() / 60 + end.getSeconds() / 3600;
      let durationHours = (end - start) / (60 * 60 * 1000);
      if (durationHours <= 0) durationHours = 1;
      const visibleStart = Math.max(gridStart, startHours);
      const visibleEnd = Math.min(gridEnd, endHours);
      const visibleDuration = Math.max(0, visibleEnd - visibleStart);
      if (visibleDuration <= 0) return null;
      const topHours = visibleStart - gridStart;
      const topPercent = (topHours / HOURS_COUNT) * 100;
      const heightPercent = (visibleDuration / HOURS_COUNT) * 100;
      return { top: `${topPercent}%`, height: `${heightPercent}%` };
    };
    const getEventPosition = (ev, dayDate) => {
      const start = new Date(ev.start_time);
      const eEnd = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
      return getEventPositionForTimes(dayDate, start, eEnd);
    };
    const days = isWeek ? getWeekDays(getWeekStart(focusDate)) : [new Date(focusDate)];
    return (
      <div className={`calendar-time-grid ${!isWeek ? 'calendar-time-grid--day' : ''}`}>
        <div className="calendar-time-col calendar-time-col--header">
          <div className="calendar-time-cell calendar-time-cell--corner" />
          <div className="calendar-time-slots calendar-time-slots--header">
            {HOURS.map((h) => (
              <div key={h} className="calendar-time-cell calendar-time-cell--hour">
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>
        {days.map((d) => {
          const key = toDateKey(d);
          const dayEvents = (eventsByDate[key] || []).sort(
            (a, b) => new Date(a.start_time) - new Date(b.start_time)
          );
          let selTop = 0, selH = 0;
          if (dragState) {
            const sk = dragState.startDayKey;
            const ck = dragState.currentDayKey;
            const sh = dragState.startHour;
            const ch = dragState.currentHour;
            const [earlyKey, lateKey] = sk <= ck ? [sk, ck] : [ck, sk];
            const [earlyH, lateH] = sk <= ck ? [sh, ch] : [ch, sh];
            if (key >= earlyKey && key <= lateKey) {
              const dayStart = key === earlyKey ? earlyH : gridStart;
              const dayEnd = key === lateKey ? lateH : gridEnd;
              selTop = (Math.min(dayStart, dayEnd) - gridStart) / HOURS_COUNT * 100;
              selH = Math.abs(dayEnd - dayStart) / HOURS_COUNT * 100;
            }
          }

          return (
            <div key={key} className="calendar-time-col calendar-time-col--with-events" data-day-key={key}>
              <div className={`calendar-time-cell calendar-time-cell--day ${isSameDay(d, new Date()) ? 'calendar-time-cell--today' : ''}`}>
                <span className="calendar-time-day-num">{d.getDate()}</span>
                <span className="calendar-time-day-wd">{WEEKDAYS[(d.getDay() + 6) % 7]}</span>
              </div>
              <div className="calendar-time-slots" onMouseDown={handleSlotsMouseDown}>
                {HOURS.map((h) => (
                  <div key={h} className="calendar-time-cell calendar-time-slot" />
                ))}
              </div>
              {selH > 0 && (
                <div className="calendar-time-selection-layer">
                  <div className="calendar-time-selection" style={{ top: `${selTop}%`, height: `${selH}%` }} />
                </div>
              )}
              <div className="calendar-time-events-layer">
                {dayEvents.map((ev) => {
                  const isDragging = shiftDragState?.event?.id === ev.id;
                  const pos = getEventPosition(ev, d);
                  if (!pos) return null;
                  const zone = eventHoverZone?.id === ev.id ? eventHoverZone.zone : null;
                  return (
                    <div
                      key={ev.id}
                      className={`calendar-time-event event-kind--${ev.event_type || "event"} ${zone ? `calendar-time-event--shift-${zone}` : ''} ${isDragging ? 'calendar-time-event--dragging' : ''}`}
                      style={{ top: pos.top, height: pos.height }}
                      title={ev.title}
                      role="button"
                      tabIndex={0}
                      data-event-id={ev.id}
                      onClick={() => handleEventClick(ev)}
                      onKeyDown={(e) => e.key === 'Enter' && handleEventClick(ev)}
                      onMouseMove={(e) => handleEventMouseMove(e, ev.id)}
                      onMouseLeave={handleEventMouseLeave}
                      onMouseDown={(e) => handleEventMouseDown(e, ev)}
                      onTouchStart={(e) => handleEventTouchStart(e, ev)}
                    >
                      {(() => {
                        const start = new Date(ev.start_time);
                        const end = ev.end_time ? new Date(ev.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
                        const durationHours = (end - start) / (60 * 60 * 1000);
                        const showEnd = Math.abs(durationHours - 1) >= 0.1;
                        return (
                          <span className="calendar-time-event-content">
                            <span className="calendar-time-event-time">
                              {formatTime(ev.start_time)}
                              {showEnd && `-${formatTime(ev.end_time)}`}
                            </span>
                            <span className="calendar-time-event-title">{ev.title}</span>
                          </span>
                        );
                      })()}
                    </div>
                  );
                })}
                {shiftDragState && (() => {
                  const ev = shiftDragState.event;
                  const s = new Date(shiftDragState.currentStart ?? shiftDragState.originStart);
                  const end = new Date(shiftDragState.currentEnd ?? shiftDragState.originEnd);
                  const ghostPos = getEventPositionForTimes(d, s, end);
                  if (!ghostPos) return null;
                  return (
                    <div
                      className="calendar-time-event calendar-time-event--ghost"
                      style={{ top: ghostPos.top, height: ghostPos.height }}
                      aria-hidden
                    >
                      {(() => {
                        const durationHours = (end - s) / (60 * 60 * 1000);
                        const showEnd = Math.abs(durationHours - 1) >= 0.1;
                        return (
                          <span className="calendar-time-event-content">
                            <span className="calendar-time-event-time">
                              {formatTime(s)}
                              {showEnd && ` — ${formatTime(end)}`}
                            </span>
                            <span className="calendar-time-event-title">{ev.title}</span>
                          </span>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAgenda = () => {
    const key = toDateKey(focusDate);
    const dayEvents = [...(eventsByDate[key] || [])].sort((a,b) => new Date(a.start_time)-new Date(b.start_time));
    return <div className="calendar-agenda">
      <div className="calendar-agenda-week" aria-label="Дни недели">{getWeekDays(getWeekStart(focusDate)).map(day => <button key={toDateKey(day)} aria-pressed={isSameDay(day, focusDate)} className={isSameDay(day, focusDate) ? 'is-active' : ''} onClick={() => setFocusDate(day)}><span>{day.toLocaleDateString('ru-RU', {weekday:'short'})}</span><strong>{day.getDate()}</strong><small aria-label={`${(eventsByDate[toDateKey(day)] || []).length} событий`}>{(eventsByDate[toDateKey(day)] || []).length || "—"}</small></button>)}</div>
      <h3>{focusDate.toLocaleDateString('ru-RU', {day:'numeric',month:'long'})}</h3>
      {dayEvents.length ? dayEvents.map(event => <button className={`calendar-agenda-event event-kind--${event.event_type || "event"}`} key={event.id} onClick={() => setDetailEvent(event)}><time>{formatTime(event.start_time)}<small>{event.end_time && formatTime(event.end_time)}</small></time><div><strong>{compactTitle(event)}</strong>{readableDescription(event) && <p>{readableDescription(event)}</p>}</div><span aria-hidden="true">›</span></button>) : <div className="calendar-empty">На этот день событий нет. Можно запланировать что-нибудь новое.</div>}
    </div>;
  };

  const renderContent = () => {
    if (loading) return <div className="calendar-loading">Загрузка...</div>;
    if (error)
      return (
        <div className="calendar-error">
          {error}
          <button onClick={fetchEvents} className="calendar-retry">
            Повторить
          </button>
        </div>
      );
    if (events.length === 0 && view === 'list')
      return (
        <div className="calendar-empty">
          Нет событий в этом месяце. Попросите агента создать событие в чате.
        </div>
      );
    if (view === 'agenda') return renderAgenda();
    if (view === 'list') return renderList();
    if (view === 'month') return renderMonth();
    if (view === 'week' || view === 'day') return isMobile ? renderAgenda() : renderWeekOrDay(view === 'week');
    return null;
  };

  const renderHeaderPeriod = () => {
    if (!headerPeriodSpec) return null;
    const s = headerPeriodSpec;
    if (s.variant === 'plain') {
      return <span className="calendar-header-period-text">{s.text}</span>;
    }
    if (s.variant === 'dayRelative') {
      return (
        <span className="calendar-header-period-line">
          <span className="calendar-header-period-primary">{s.title}</span>
          <span className="calendar-header-period-sub">{s.sub}</span>
        </span>
      );
    }
    if (s.variant === 'dayAbsolute') {
      return (
        <span className="calendar-header-period-line">
          <span className="calendar-header-period-primary">{s.main}</span>
          <span className="calendar-header-period-year">{s.year}</span>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
      <div className="calendar-heading"><button className="Finance-btn" aria-label="Добавить событие" onClick={() => { const start = new Date(focusDate); start.setHours(10,0,0,0); const end = new Date(start); end.setHours(11); setCreateForm({startTime: formatDatetimeForApi(start), endTime: formatDatetimeForApi(end)}); }}>{isMobile ? '+' : '+ Событие'}</button></div>

        <div
          className={`calendar-view-switcher calendar-view-switcher--bar ${isMobile ? 'calendar-view-switcher--bar-mobile' : ''}`}
        >
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`calendar-view-btn ${view === v ? 'calendar-view-btn--active' : ''}`}
              onClick={() => setView(v)}
            >
              {VIEW_LABELS[v]}
            </button>
          ))}
        </div>
        {headerPeriodSpec && (
          <div
            className={`calendar-header-period-row${isMobile ? ' calendar-header-period-row--mobileNav' : ''}`}
          >
            <button type="button" onClick={prev} className="calendar-nav-btn" aria-label="Назад">
              ‹
            </button>
            <div className="calendar-header-period-center">{renderHeaderPeriod()}</div>
            <button type="button" onClick={next} className="calendar-nav-btn" aria-label="Вперёд">
              ›
            </button>
            <button type="button" onClick={today} className="calendar-nav-btn calendar-nav-btn--today">
              Сегодня
            </button>
          </div>
        )}
      </div>

      <div className={`calendar-content calendar-content--${view}`}>
        {renderContent()}
      </div>

      {isMobile && view === 'month' && selectedDayKey && (() => {
        const dayEvents = (eventsByDate[selectedDayKey] || []).sort(
          (a, b) => new Date(a.start_time) - new Date(b.start_time)
        );
        const [y, m, day] = selectedDayKey.split('-').map(Number);
        const dayLabel = new Date(y, m - 1, day).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
        return (
          <div className="calendar-day-detail-overlay">
            <div className="calendar-day-detail">
              <div className="calendar-day-detail-header">
                <button type="button" className="calendar-day-detail-back" onClick={() => setSelectedDayKey(null)} aria-label="Назад">
                  ‹ Назад
                </button>
                <h3 className="calendar-day-detail-title">События дня {dayLabel}</h3>
              </div>
              <ul className="calendar-day-detail-list">
                {dayEvents.length === 0 ? (
                  <li className="calendar-day-detail-empty">Нет событий</li>
                ) : (
                  dayEvents.map((ev) => (
                    <li
                      key={ev.id}
                      className="calendar-event calendar-event--clickable"
                      onClick={() => { setDetailEvent(ev); setSelectedDayKey(null); }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setDetailEvent(ev); setSelectedDayKey(null); } }}
                    >
                      <div className="calendar-event-title">{ev.title}</div>
                      <div className="calendar-event-time">
                        {formatTime(ev.start_time)}
                        {ev.end_time && ` — ${formatTime(ev.end_time)}`}
                      </div>
                      {ev.description && <div className="calendar-event-desc">{ev.description}</div>}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        );
      })()}

      {createForm && (
        <div className="calendar-modal-overlay" onClick={() => setCreateForm(null)}>
          <CalendarDialog className="calendar-modal" label="Новое событие" onClose={() => setCreateForm(null)}>
            <h3>Новое событие</h3>
            <form onSubmit={handleCreateSubmit}>
              <div className="calendar-form-row">
                <label>Название</label>
                <input name="title" type="text" required placeholder="Название события" autoFocus />
              </div>
              <div className="calendar-form-row">
                <label>Описание</label>
                <textarea name="description" placeholder="Описание (необязательно)" rows={3} />
              </div>
              <div className="calendar-form-row">
                <label>Начало</label>
                <input aria-label="Начало" name="start_time" type="datetime-local" defaultValue={toDatetimeLocal(createForm.startTime)} required />
              </div>
              <div className="calendar-form-row">
                <label>Конец</label>
                <input aria-label="Конец" name="end_time" type="datetime-local" defaultValue={toDatetimeLocal(createForm.endTime)} required />
              </div>
              <div className="calendar-form-actions">
                <button type="button" className="calendar-form-btn calendar-form-btn--cancel" onClick={() => setCreateForm(null)}>
                  Отмена
                </button>
                <button type="submit" className="calendar-form-btn calendar-form-btn--submit">
                  Создать
                </button>
              </div>
            </form>
          </CalendarDialog>
        </div>
      )}

      {detailEvent && (
        <div className="calendar-modal-overlay" onClick={() => setDetailEvent(null)}>
          <CalendarDialog className="calendar-modal calendar-event-preview" label="Просмотр события" onClose={() => setDetailEvent(null)}>
            <button type="button" className="calendar-preview-close calendar-form-btn" onClick={() => setDetailEvent(null)} aria-label="Закрыть событие">Закрыть</button>
            <span className={`calendar-event-kind event-kind--${detailEvent.event_type || "event"}`}>{detailEvent.event_type === 'payment' ? 'Оплата' : detailEvent.event_type === 'topup' ? 'Пополнение' : 'Событие'}</span>
            <h3>{detailEvent.title}</h3>
            <p><time>{new Date(detailEvent.start_time).toLocaleDateString('ru-RU', {weekday:'long', day:'numeric', month:'long', year:'numeric'})} · {formatTime(detailEvent.start_time)}{detailEvent.end_time && ` — ${formatTime(detailEvent.end_time)}`}</time></p>
            {detailEvent.description && <p className="calendar-preview-description">{readableDescription(detailEvent)}</p>}
            <button type="button" className="calendar-form-btn calendar-form-btn--submit" onClick={() => { setEditForm(detailEvent); setDetailEvent(null); }}>Редактировать</button>
          </CalendarDialog>
        </div>
      )}

      {editForm && (
        <div className="calendar-modal-overlay" onClick={() => setEditForm(null)}>
          <CalendarDialog className="calendar-modal calendar-modal--edit" label="Редактирование события" onClose={() => setEditForm(null)}>
            <h3>Редактирование события</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="calendar-form-row">
                <label>Название</label>
                <input name="title" type="text" required defaultValue={editForm.title} />
              </div>
              <div className="calendar-form-row">
                <label>Описание</label>
                <textarea name="description" defaultValue={editForm.description || ''} rows={3} />
              </div>
              <div className="calendar-form-row">
                <label>Начало</label>
                <input
                  name="start_time"
                  type="datetime-local"
                  defaultValue={toDatetimeLocal(editForm.start_time)}
                  step={600}
                />
              </div>
              <div className="calendar-form-row">
                <label>Конец</label>
                <input
                  name="end_time"
                  type="datetime-local"
                  defaultValue={toDatetimeLocal(editForm.end_time)}
                  step={600}
                />
              </div>
              <div className="calendar-form-actions">
                <button type="button" className="calendar-form-btn calendar-form-btn--danger" onClick={() => deleteEvent(editForm.id)}>
                  Удалить
                </button>
                <div className="calendar-form-actions-right">
                  <button type="button" className="calendar-form-btn calendar-form-btn--cancel" onClick={() => setEditForm(null)}>
                    Отмена
                  </button>
                  <button type="submit" className="calendar-form-btn calendar-form-btn--submit">
                    Сохранить
                  </button>
                </div>
              </div>
            </form>
          </CalendarDialog>
        </div>
      )}
    </div>
  );
};

export default Calendar;
